import { StatusBar } from "expo-status-bar";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../src/theme";
import { useEffectiveSession } from "../src/use-effective-session";
import { useSelectedVertical } from "../src/use-selected-vertical";
import { getStoredSelection } from "../src/selected-vertical-store";
import { trpc } from "../src/trpc";

// App root. Per the 2026 brand refresh this is a thin dispatcher, not
// a content page — the marketing site at apps/seo owns the unauthed
// landing, and the paid product surface lives at /home. Decisions:
//
//   unauth                           → /welcome (pick a credential)
//   auth + no paid entitlement       → /welcome
//   auth + at least one entitlement  → /home
//
// The legacy ?claim= flow (SEO inline-quiz attempts threaded through a
// magic-link callbackURL) still runs here so attempts persist before
// the user lands inside the product.

interface ClaimAttempt {
  questionId: string;
  answer: "A" | "B" | "C" | "D";
  msSpent: number;
}

// Reverse the URL-safe base64 alphabet (-→+, _→/) and restore '='
// padding before decoding. Both encoders (apps/seo quiz.ts and
// apps/client anon-attempts.ts) now emit URL-safe base64; this is also a
// no-op on legacy standard-base64 payloads (which never contain '-'/'_'
// and arrive already padded), so old in-flight magic links still decode.
function fromUrlSafeBase64(raw: string): string {
  const swapped = raw.replace(/-/g, "+").replace(/_/g, "/");
  const pad = swapped.length % 4;
  return pad === 0 ? swapped : swapped + "=".repeat(4 - pad);
}

function tryDecodeClaim(raw: string): ClaimAttempt[] | null {
  try {
    const b64 = fromUrlSafeBase64(raw);
    const decoded =
      typeof atob === "function"
        ? atob(b64)
        : Buffer.from(b64, "base64").toString("utf8");
    const parsed = JSON.parse(decoded) as unknown;
    if (!Array.isArray(parsed)) return null;
    const out: ClaimAttempt[] = [];
    for (const item of parsed) {
      if (typeof item !== "object" || item === null) return null;
      const obj = item as Record<string, unknown>;
      if (typeof obj.questionId !== "string") return null;
      if (
        obj.answer !== "A" &&
        obj.answer !== "B" &&
        obj.answer !== "C" &&
        obj.answer !== "D"
      )
        return null;
      if (typeof obj.msSpent !== "number") return null;
      out.push({
        questionId: obj.questionId,
        answer: obj.answer,
        msSpent: Math.floor(Math.max(0, obj.msSpent)),
      });
    }
    return out.length > 0 && out.length <= 10 ? out : null;
  } catch {
    return null;
  }
}

export default function Index(): JSX.Element {
  const session = useEffectiveSession();
  const selected = useSelectedVertical();
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{
    claim?: string;
    vertical?: string;
    jurisdiction?: string;
  }>();
  const [claimDone, setClaimDone] = useState(false);
  // Guard against the claim mutation re-firing on param churn — the
  // effect re-runs when `params.claim` changes (e.g. our own
  // setParams({ claim: undefined })), so claim at most once per mount.
  const claimedRef = useRef(false);

  // Best-effort claim of anonymous SEO-quiz attempts. Failure is silent
  // and falls through to the routing decision below — better to land
  // the user somewhere than block on a bad payload.
  useEffect(() => {
    if (claimedRef.current) return;
    if (!session.user?.id) {
      // No user yet. Don't declare the claim "done" while the session
      // is still resolving — a magic-link callback may be mid-flight,
      // and setting claimDone prematurely lets the routing effect fire
      // before the session (and any claim) settles (#8).
      if (!session.isPending) setClaimDone(true);
      return;
    }
    const raw = typeof params.claim === "string" ? params.claim : null;
    if (!raw) {
      setClaimDone(true);
      return;
    }
    const attempts = tryDecodeClaim(raw);
    if (!attempts) {
      router.setParams({ claim: undefined });
      setClaimDone(true);
      return;
    }
    claimedRef.current = true;
    void (async () => {
      try {
        await trpc.questions.claimAnonymous.mutate({ attempts });
      } catch {
        // non-fatal
      } finally {
        router.setParams({ claim: undefined });
        setClaimDone(true);
      }
    })();
  }, [session.user?.id, session.isPending, params.claim]);

  // Wait for the session check + (if applicable) the claim round trip
  // to finish before deciding. Without `claimDone` the redirect might
  // fire before the attempts persist.
  useEffect(() => {
    if (session.isPending || !claimDone || selected.isLoading) return;
    // Entitlement fetch failed for a SIGNED-IN user — entitlement state is
    // unknown, not empty. Hold on the spinner rather than routing a paid
    // user to /welcome as if they have nothing (#6). For a logged-out user
    // the entitlement.list 401 is expected (no session), not a failure, so
    // it must NOT block the normal route to /welcome.
    if (selected.loadError && session.user) return;
    // Honor a deep link from the marketing site
    // (?vertical=<slug>[&jurisdiction=<code>]). The credential is already
    // chosen, so hand the params to /welcome, which has the vertical metadata
    // to route on to the state picker or straight into practice without
    // re-asking. Without this, the chosen credential is silently dropped and
    // the user is dumped back on the picker.
    const deepVertical =
      typeof params.vertical === "string" ? params.vertical : null;
    const deepJurisdiction =
      typeof params.jurisdiction === "string" ? params.jurisdiction : null;
    if (deepVertical) {
      router.replace({
        pathname: "/welcome",
        params: {
          vertical: deepVertical,
          ...(deepJurisdiction ? { jurisdiction: deepJurisdiction } : {}),
        },
      });
      return;
    }
    const hasPaidEntitlement = selected.entitlements.length > 0;
    if (session.user && hasPaidEntitlement) {
      router.replace("/home");
      return;
    }
    // No paid entitlement (a just-signed-up free user, or still anonymous).
    // Restore the credential they picked before the auth round-trip stripped
    // the URL params, so they resume their exam instead of re-picking from a
    // blank slate. /welcome fast-forwards straight to the state picker or
    // practice when handed these params.
    const stored = getStoredSelection();
    if (stored) {
      router.replace({
        pathname: "/welcome",
        params: {
          vertical: stored.verticalSlug,
          ...(stored.jurisdictionCode
            ? { jurisdiction: stored.jurisdictionCode }
            : {}),
        },
      });
      return;
    }
    router.replace("/welcome");
  }, [
    session.isPending,
    session.user,
    selected.isLoading,
    selected.loadError,
    selected.entitlements.length,
    claimDone,
    params.vertical,
    params.jurisdiction,
  ]);

  return (
    <View style={styles.container}>
      <ActivityIndicator color={colors.accentSoftText} />
      <Text style={styles.caption}>One moment…</Text>
      <StatusBar style="auto" />
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[3],
    },
    caption: {
      ...textStyle.caption,
      color: colors.textMuted,
      fontFamily: fontFamily.regular,
    },
  });
}
